import { useCallback, useRef } from 'react';
import type { AnnotationAction } from '../annotationReducer';

interface MoveState {
  startX: number;
  startY: number;
  lastDx: number;
  lastDy: number;
}

interface UseShapeMoveOptions {
  selectedIds: Set<string>;
  dispatch: React.Dispatch<AnnotationAction>;
  containerWidth: number;
  containerHeight: number;
}

export function useShapeMove({ selectedIds, dispatch, containerWidth, containerHeight }: UseShapeMoveOptions) {
  const moveState = useRef<MoveState | null>(null);

  const startMove = useCallback(
    (clientX: number, clientY: number) => {
      moveState.current = { startX: clientX, startY: clientY, lastDx: 0, lastDy: 0 };
    },
    [],
  );

  const onMovePointer = useCallback(
    (clientX: number, clientY: number) => {
      const state = moveState.current;
      if (!state || containerWidth === 0 || containerHeight === 0) return;

      const totalDxNorm = (clientX - state.startX) / containerWidth;
      const totalDyNorm = (clientY - state.startY) / containerHeight;

      const incrementDx = totalDxNorm - state.lastDx;
      const incrementDy = totalDyNorm - state.lastDy;

      if (incrementDx === 0 && incrementDy === 0) return;

      state.lastDx = totalDxNorm;
      state.lastDy = totalDyNorm;

      dispatch({
        type: 'MOVE_SHAPES',
        ids: Array.from(selectedIds),
        dx: incrementDx,
        dy: incrementDy,
      });
    },
    [selectedIds, containerWidth, containerHeight, dispatch],
  );

  const endMove = useCallback(() => {
    moveState.current = null;
  }, []);

  const nudge = useCallback(
    (dx: number, dy: number) => {
      if (selectedIds.size === 0 || containerWidth === 0 || containerHeight === 0) return;
      dispatch({
        type: 'MOVE_SHAPES',
        ids: Array.from(selectedIds),
        dx: dx / containerWidth,
        dy: dy / containerHeight,
      });
    },
    [selectedIds, containerWidth, containerHeight, dispatch],
  );

  const isMoving = useCallback(() => moveState.current !== null, []);

  return { startMove, onMovePointer, endMove, nudge, isMoving };
}
